/**
 * Platform overview — Wave 1a / W1a-16.
 *
 * Single hook backing the sysadmin platform dashboard at `/admin/platform`.
 * Pulls cross-tenant aggregations (tenant counts, user counts, sysadmin
 * counts, storage totals), recent platform-audit activity, and platform
 * alerts in a single roundtrip.
 *
 * Refetches every 30 s so the dashboard feels live without spamming the
 * audit chain.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Wire types ──────────────────────────────────────────────────────────────

export interface TenantStats {
  total: number;
  active: number;
  pending: number;
  suspended: number;
  deleted: number;
}

export interface UserStats {
  total: number;
  active: number;
  invited: number;
  suspended: number;
}

export interface SysadminStats {
  active: number;
  inactive: number;
}

export interface StorageStats {
  documentsCount: number;
  conversationsCount: number;
  messagesCount: number;
}

export interface RecentActivityRow {
  id: string;
  sysadminEmail: string;
  action: string;
  category: string;
  targetTenantId: string | null;
  createdAt: string;
}

export type PlatformAlertCode =
  | 'SYSADMIN_TOTP_PENDING'
  | 'TENANT_PENDING_PROVISIONING'
  | 'SOFT_DELETE_PURGE_DUE_SOON';

export interface PlatformAlert {
  code: PlatformAlertCode;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  count?: number;
}

export interface PlatformOverviewResponse {
  tenants: TenantStats;
  users: UserStats;
  sysadmins: SysadminStats;
  storage: StorageStats;
  recentPlatformActivity: RecentActivityRow[];
  alerts: PlatformAlert[];
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function usePlatformOverview() {
  return useQuery<PlatformOverviewResponse, Error>({
    queryKey: ['platform-overview'],
    queryFn: () =>
      apiClient.get<PlatformOverviewResponse>('/admin/platform/overview'),
    // 30-second auto-refresh keeps stat cards lively without saturating
    // the audit chain (every refetch writes a PLATFORM_OVERVIEW_READ row).
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}
